import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { AdCreative, Permissions } from '@marketing-milk/interfaces'
import { sessionContext, useGetBusiness } from '@marketing-milk/frontend'
import { MobilePreview } from '../mobile/MobilePreview'
import { isLocallyRenderable, LocalAdPreview } from '../local'

export interface CreativePreviewProps {
  /** Source of the local fallback render (copy, image/video, headline, CTA). */
  creative: AdCreative
  /**
   * Caller-extracted FB iframe src (`getIFrameSource(html)`). The sprint-packet
   * modal passes its fresh-gated source; the others pass the raw extraction. An
   * empty string means no live preview is available.
   */
  iframeSource: string
  /** True while the initial preview fetch is in flight (drives the "unavailable" decision). */
  loading: boolean
  /** True while a forced refresh is in flight (drives the "unavailable" decision). */
  refreshing: boolean
  /** Banner Retry handler — modal passes `refreshPreview`, others pass `retrievePreview`. */
  onRetry: () => void
  /**
   * Suppress the "preview unavailable" banner, keeping the silent local fallback.
   * For client-facing surfaces: a client being asked to approve the work doesn't
   * need to be told our Facebook connection is unhappy, and can't act on it. Leave
   * it off anywhere staff are looking, where the banner is the only signal that
   * the render isn't Facebook's own.
   */
  hideUnavailableNotice?: boolean
  /** Sizing passthroughs forwarded to `MobilePreview`. */
  width?: number | string
  height?: number | string
  frameless?: boolean
  fluidWidth?: boolean
  /** Fit the complete ad into a dialog at this width, scaling it down if it is taller than
   *  the window. `reservedHeight` is room the caller has already spoken for below the ad. */
  fitToWindow?: { width: number; reservedHeight?: number }
}

// The card we render locally has no Like / Comment / Share row; Facebook's render does. The
// frame is given that height on top of the card so the live preview is not cropped when it
// replaces the card, and so the box does not change size at the moment it does.
const FB_ACTION_BAR_HEIGHT = 48

// Room the dialog's own margins take out of the window.
const DIALOG_CHROME_HEIGHT = 24

// Stands in for the card's height until it has rendered and been measured.
const INITIAL_CARD_HEIGHT = 680

// Space between the preview and the unavailable notice below it.
const COLUMN_GAP = 10

function FittedPreview({
  iframeSource,
  width,
  reserved,
  fallback,
}: {
  iframeSource: string
  width: number
  reserved: number
  fallback: React.ReactNode
}) {
  const fallbackRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [availableWidth, setAvailableWidth] = useState<number>()
  const [loadedSource, setLoadedSource] = useState<string>()
  const [cardHeight, setCardHeight] = useState(0)
  const [windowHeight, setWindowHeight] = useState(() => window.innerHeight)
  const showLivePreview = !!iframeSource && loadedSource === iframeSource

  useLayoutEffect(() => {
    const card = fallbackRef.current
    if (!card) return
    const measure = () => setCardHeight(card.offsetHeight)
    measure()
    // The card grows as the creative's image loads, so its height is watched rather than read
    // once. jsdom has no ResizeObserver, where the single measurement above is enough.
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(card)
    return () => observer.disconnect()
  }, [fallback])

  // The dialog holding this is narrower than the preview on a small viewport, where scaling
  // for height alone left the ad clipped at the paper's edge.
  useLayoutEffect(() => {
    const parent = containerRef.current?.parentElement
    if (!parent) return
    const measure = () => setAvailableWidth(parent.clientWidth)
    measure()
    if (typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(measure)
    observer.observe(parent)
    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const onResize = () => setWindowHeight(window.innerHeight)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  // Facebook's frame is cross-origin and reports no usable content height, so the locally
  // rendered card underneath sizes it — it is the same ad, so it is a far closer fit than a
  // fixed floor, which left a band of Facebook's blank page below every short ad.
  // Carousel and dynamic creatives have no local card, so the measured element is the short
  // "Loading preview…" line instead; those fall back to the floor rather than to its height.
  const contentHeight =
    fallback && cardHeight ? cardHeight + FB_ACTION_BAR_HEIGHT : INITIAL_CARD_HEIGHT

  // Whatever sits below the ad in the dialog is subtracted first, or a full-height ad scales to
  // exactly the window and pushes that content out of it — the scrolling this exists to avoid.
  const available = Math.max(200, windowHeight - DIALOG_CHROME_HEIGHT - reserved)

  // An ad taller than the window is scaled down to fit rather than made to scroll: a couple of
  // percent is invisible, where a scrollbar through a preview is not. It never scales up.
  const scale = Math.min(1, available / contentHeight, availableWidth ? availableWidth / width : 1)

  return (
    <div
      ref={containerRef}
      style={{
        width: width * scale,
        height: contentHeight * scale,
        margin: '0 auto',
        position: 'relative',
      }}
    >
      <div
        style={{
          width,
          height: contentHeight,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      >
        <div ref={fallbackRef} style={{ visibility: showLivePreview ? 'hidden' : 'visible' }}>
          {fallback ?? (
            <p role="status" style={{ padding: 24 }}>
              Loading preview…
            </p>
          )}
        </div>
        {iframeSource && (
          <iframe
            title="Facebook ad preview"
            src={iframeSource}
            width={width}
            height={contentHeight}
            scrolling="no"
            onLoad={() => setLoadedSource(iframeSource)}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              border: 0,
              visibility: showLivePreview ? 'visible' : 'hidden',
            }}
          />
        )}
      </div>
    </div>
  )
}

// Facebook's own chrome greys, on a light chip of their own. The chip is what
// makes them safe: this banner also renders on the dark sprint-packet review
// surface, where near-black button ink on a transparent background left Retry
// invisible.
const bannerColors = {
  surface: '#f0f2f5',
  text: '#65676b',
  border: '#dadde1',
  buttonInk: '#050505',
  link: '#1877f2',
}

/**
 * Universal, controlled creative-preview surface: the live Facebook mobile-feed
 * iframe layered over the network-free `LocalAdPreview` base, plus an inline
 * "Live Facebook preview unavailable" banner that replaces the hook's error toast
 * wherever the live preview can't be shown.
 *
 * Controlled by design: each consumer already owns a `useFetchCreativePreview`
 * hook and any approval gating, so this component takes the resolved preview state
 * as props and owns only the viewport, the local fallback, the unavailable banner,
 * and the (internal-only) authorizer link. Generalized from the sprint packet's
 * own modal so the same degrade-gracefully behavior applies on the creatives
 * page, campaign summary, and campaign ad-set surfaces.
 */
export function CreativePreview({
  creative,
  iframeSource,
  loading,
  refreshing,
  onRetry,
  hideUnavailableNotice = false,
  width,
  height,
  frameless,
  fluidWidth,
  fitToWindow,
}: CreativePreviewProps) {
  const business = useGetBusiness()
  const { user } = useContext(sessionContext)

  // The live FB fetch has settled with no usable preview (down / token expired /
  // timed out). The local fallback is the only thing on screen.
  const fbUnavailable = !iframeSource && !loading && !refreshing
  const localRenderable = isLocallyRenderable(creative.format)

  // The local, Facebook-independent base layer paints instantly and stays visible
  // whenever the live FB iframe is unavailable. For carousel/dynamic we only have a
  // placeholder, so we withhold it while FB is still loading (MobilePreview shows
  // its spinner) and show it once FB has settled unavailable — never masking the
  // spinner with a non-authoritative placeholder.
  const fallback =
    localRenderable || fbUnavailable ? (
      <LocalAdPreview
        creative={creative}
        pageName={business.businessName}
        pageImageURL={business.fbPageImgURL}
      />
    ) : undefined

  // Every observed preview failure has been a Facebook token/authorization
  // problem, so we surface a link to the admin Authorizers page — but only for
  // internal staff (external sprint-packet viewers never see it).
  const showAuthorizerLink = !!user?.permissions.includes(Permissions.AccessAdminApp)

  // The notice sits below the ad, so a fitted preview has to leave room for it. It is measured
  // rather than given a constant because its text wraps at narrow widths.
  const showNotice = fbUnavailable && !hideUnavailableNotice
  const noticeRef = useRef<HTMLDivElement>(null)
  const [noticeHeight, setNoticeHeight] = useState(0)
  useLayoutEffect(() => {
    setNoticeHeight(noticeRef.current ? noticeRef.current.offsetHeight + COLUMN_GAP : 0)
  }, [showNotice, showAuthorizerLink])

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: COLUMN_GAP,
        width: '100%',
        minWidth: 0,
      }}
    >
      {fitToWindow ? (
        <FittedPreview
          iframeSource={iframeSource}
          width={fitToWindow.width}
          reserved={(fitToWindow.reservedHeight ?? 0) + noticeHeight}
          fallback={fallback}
        />
      ) : (
        <MobilePreview
          iframeSource={iframeSource}
          width={width}
          height={height}
          frameless={frameless}
          fluidWidth={fluidWidth}
          fallback={fallback}
        />
      )}
      {showNotice && (
        <div
          ref={noticeRef}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
            background: bannerColors.surface,
            border: `1px solid ${bannerColors.border}`,
            borderRadius: 8,
            padding: '8px 12px',
            color: bannerColors.text,
            fontSize: 13,
            lineHeight: 1.4,
            textAlign: 'center',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <span>Live Facebook preview unavailable — showing local preview.</span>
            <button
              type="button"
              onClick={onRetry}
              style={{
                border: `1px solid ${bannerColors.border}`,
                background: '#fff',
                color: bannerColors.buttonInk,
                borderRadius: 6,
                padding: '4px 12px',
                fontSize: 13,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              Retry
            </button>
          </div>
          {showAuthorizerLink && (
            <a
              href={`${process.env.REACT_APP_ADMIN}/business/authorizers/list`}
              target="_blank"
              rel="noreferrer"
              style={{ color: bannerColors.link, fontSize: 12 }}
            >
              The Facebook authorization may need attention — check authorizers ↗
            </a>
          )}
        </div>
      )}
    </div>
  )
}
